import React, { useEffect, useState } from 'react';

interface MemberDetailsModalProps {
  isOpen: boolean;
  title: string;
  subtitle?: string;
  onRequestClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
}

const MemberDetailsModal: React.FC<MemberDetailsModalProps> = ({
  isOpen,
  title,
  subtitle,
  onRequestClose,
  children,
  footer,
}) => {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    return window.innerWidth <= 768;
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onRequestClose();
      }
    };

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleEscape);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onRequestClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        background: 'rgba(15, 23, 42, 0.45)',
        display: 'flex',
        alignItems: isMobile ? 'stretch' : 'center',
        justifyContent: 'center',
        padding: isMobile ? 0 : 16,
      }}
      onClick={onRequestClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        style={{
          width: isMobile ? '100vw' : 'min(85vw, 1240px)',
          height: isMobile ? '100vh' : 'min(90vh, 940px)',
          borderRadius: isMobile ? 0 : 14,
          border: isMobile ? 'none' : '1px solid #dbe4f0',
          background: '#fff',
          boxShadow: isMobile ? 'none' : '0 20px 40px rgba(15, 23, 42, 0.24)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
        onClick={(event) => event.stopPropagation()}
      >
        <div style={{ padding: '16px 18px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: '1.15rem', fontWeight: 700, color: '#0f172a' }}>{title}</div>
            {subtitle && <div style={{ marginTop: 4, color: '#64748b', fontSize: '0.9rem' }}>{subtitle}</div>}
          </div>
          <button
            type="button"
            onClick={onRequestClose}
            style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', color: '#334155', cursor: 'pointer' }}
          >
            Close
          </button>
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
          {children}
        </div>

        {footer && (
          <div style={{ padding: '12px 16px', borderTop: '1px solid #e2e8f0', background: '#fff' }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export default MemberDetailsModal;
